import {
  RenewalTestEmailKind,
  useOrganizationSendRenewalTestEmailMutation,
  useOrganizationStaffQuery,
  UserWithPictureTc,
} from "gql/generated";
import { Mail } from "iconoir-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { WhereaboutsModal } from "shared/components";
import { Flex } from "styled-system/jsx";
import { Select, Text, useToastContext } from "@ttc3k/trekker";

export type RenewalEmailKind =
  | "instructions"
  | "expired"
  | "success"
  | "overdue"
  | "cancelled"
  | "custom";

const EMAIL_KIND_MAP: Record<RenewalEmailKind, RenewalTestEmailKind> = {
  instructions: RenewalTestEmailKind.RenewalInstructions,
  expired: RenewalTestEmailKind.RenewalExpired,
  success: RenewalTestEmailKind.RenewalSuccess,
  overdue: RenewalTestEmailKind.RenewalOverdue,
  cancelled: RenewalTestEmailKind.RenewalCancelled,
  custom: RenewalTestEmailKind.Custom,
};

type RenewalSendTestEmailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  emailKind: RenewalEmailKind;
  subject: string;
  body: string;
  hideRenewalDateLine?: boolean;
  /** Button label being edited; undefined sends the email's default. */
  ctaLabel?: string;
  /** Custom emails: button link being edited; undefined links to the renewal page. */
  ctaUrl?: string;
  /** Custom emails: leave out the "Hi <name>," line. */
  hideGreeting?: boolean;
};

export const RenewalSendTestEmailModal = ({
  isOpen,
  onClose,
  organizationId,
  emailKind,
  subject,
  body,
  hideRenewalDateLine = false,
  ctaLabel,
  ctaUrl,
  hideGreeting,
}: RenewalSendTestEmailModalProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.RENEWAL.TEST_MODAL",
  });

  const { toastFactory } = useToastContext();
  const [recipientUserId, setRecipientUserId] = useState("");

  const { data: staffData } = useOrganizationStaffQuery({
    variables: { organizationId },
    skip: !isOpen || !organizationId,
  });

  const [sendTestEmail, { loading }] =
    useOrganizationSendRenewalTestEmailMutation();

  useEffect(() => {
    if (!isOpen) setRecipientUserId("");
  }, [isOpen]);

  const recipientOptions = useMemo(
    () =>
      ((staffData?.organizationStaff ?? []) as UserWithPictureTc[])
        .filter(user => user.email)
        .map(user => ({
          label: `${user.firstName} ${user.lastName} (${user.email})`,
          value: user._id,
        })),
    [staffData?.organizationStaff],
  );

  const recipientCollection = useMemo(
    () => Select.createCollection({ items: recipientOptions }),
    [recipientOptions],
  );

  const onSend = useCallback(async () => {
    if (!recipientUserId) return;

    try {
      await sendTestEmail({
        variables: {
          organizationId,
          recipientUserId,
          emailKind: EMAIL_KIND_MAP[emailKind],
          subject: subject || undefined,
          body: body || undefined,
          hideRenewalDateLine,
          ctaLabel,
          ctaUrl: ctaUrl || undefined,
          hideGreeting: hideGreeting || undefined,
        },
      });

      toastFactory.create({
        title: t("SUCCESS_TITLE"),
        description: t("SUCCESS_DESCRIPTION"),
      });
      onClose();
    } catch (err) {
      toastFactory.create({
        title: t("ERROR_TITLE"),
        description:
          err instanceof Error ? err.message : t("ERROR_DESCRIPTION"),
      });
    }
  }, [
    body,
    ctaLabel,
    ctaUrl,
    hideGreeting,
    emailKind,
    hideRenewalDateLine,
    onClose,
    organizationId,
    recipientUserId,
    sendTestEmail,
    subject,
    t,
    toastFactory,
  ]);

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={onClose}
      title={t("TITLE")}
      description={t("DESCRIPTION")}
      HeaderIcon={Mail}
      size="md"
      saveButtonCopy={t("SEND")}
      cancelButtonCopy={t("CANCEL")}
      saveButtonProps={{
        visual: "accent.blue",
        disabled: !recipientUserId || !organizationId,
        isLoading: loading,
        onClick: onSend,
      }}
      cancelButtonProps={{ onClick: onClose }}
      isOverflowVisible
      isMaxZIndex
    >
      <Flex flexDir="column" gap="150">
        <Text visual="bodySemiBold" color="text.dark">
          {t("RECIPIENT_LABEL")}
        </Text>
        {recipientOptions.length === 0 ? (
          <Text visual="smallRegular" color="text.light">
            {t("NO_RECIPIENTS")}
          </Text>
        ) : (
          <Select.Root
            collection={recipientCollection}
            value={recipientUserId ? [recipientUserId] : []}
            onValueChange={({ value }: { value: string[] }) =>
              setRecipientUserId(value[0] ?? "")
            }
          >
            {recipientCollection.items.map(option => (
              <Select.Item key={option.value} item={option} />
            ))}
          </Select.Root>
        )}
      </Flex>
    </WhereaboutsModal>
  );
};
